import React from "react";
import { PAGE_SIZE_OPTIONS } from "../../utils/usePagination";
import styles from "./PaginationBar.module.css";

/** Page numbers to show: first, last, and a window around the current page, with gaps as null. */
const pageWindow = (page, pageCount) => {
  const sorted = [...new Set([1, pageCount, page - 1, page, page + 1])]
    .filter((p) => p >= 1 && p <= pageCount)
    .sort((a, b) => a - b);

  return sorted.flatMap((p, index) => (index > 0 && p - sorted[index - 1] > 1 ? [null, p] : [p]));
};

/**
 * "Showing 21–40 of 87 orders", rows-per-page, and page buttons for a
 * usePagination() result — hidden when everything fits on one page.
 */
const PaginationBar = ({ pagination, noun }) => {
  const { page, pageCount, pageSize, total, setPage, setPageSize } = pagination;

  if (total <= PAGE_SIZE_OPTIONS[0]) return null;

  const first = (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);

  return (
    <nav className={styles.bar} aria-label={`${noun} pages`}>
      <div className={styles.summary}>
        <span>
          Showing <strong>{first}–{last}</strong> of <strong>{total}</strong> {noun}
        </span>
        <label className={styles.pageSize}>
          Per page
          <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
        </label>
      </div>

      {pageCount > 1 && (
        <div className={styles.pages}>
          <button type="button" disabled={page === 1} onClick={() => setPage(page - 1)}>‹ Prev</button>
          {pageWindow(page, pageCount).map((p, index) =>
            p === null ? (
              <span key={`gap-${index}`} className={styles.gap}>…</span>
            ) : (
              <button
                key={p}
                type="button"
                aria-current={p === page ? "page" : undefined}
                className={p === page ? styles.current : undefined}
                onClick={() => setPage(p)}
              >
                {p}
              </button>
            )
          )}
          <button type="button" disabled={page === pageCount} onClick={() => setPage(page + 1)}>Next ›</button>
        </div>
      )}
    </nav>
  );
};

export default PaginationBar;
